/**
 * 分享数据获取 Hook
 * 根据令牌从 API 加载分享数据，提供三态管理（加载/成功/错误）
 */
import { useState, useEffect, useRef } from 'react';

import type { ShareResponse } from '@photo-process/shared';

import { fetchShareByToken, ApiError } from '../services/api';

/** useShare Hook 返回值类型 */
export interface UseShareResult {
  /** 分享数据，加载中或出错时为 null */
  readonly share: ShareResponse | null;
  /** 是否正在加载 */
  readonly isLoading: boolean;
  /** 错误信息，无错误时为 null */
  readonly error: string | null;
  /** 错误类型，无错误时为 null */
  readonly errorType: 'NOT_FOUND' | 'NETWORK' | 'SERVER' | null;
}

/**
 * 分享数据获取 Hook
 * 挂载时自动根据 token 获取分享数据
 *
 * @param token - 分享令牌，undefined 时不发起请求
 * @returns 分享数据、加载状态和错误信息
 *
 * @example
 * ```tsx
 * const { share, isLoading, error } = useShare(token);
 * ```
 */
export function useShare(token: string | undefined): UseShareResult {
  const [share, setShare] = useState<ShareResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<UseShareResult['errorType']>(null);

  // 防止组件卸载后更新状态
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // token 未提供时直接设置错误状态
    if (!token) {
      setIsLoading(false);
      setError('分享链接无效');
      setErrorType('NOT_FOUND');
      return;
    }

    // 局部变量捕获非空 token，供异步函数使用
    const validToken = token;

    async function loadShare(): Promise<void> {
      try {
        setIsLoading(true);
        setError(null);
        setErrorType(null);

        const data = await fetchShareByToken(validToken);

        if (!isMountedRef.current) return;

        setShare(data);
      } catch (err) {
        if (!isMountedRef.current) return;

        if (err instanceof ApiError) {
          setError(err.message);
          setErrorType(err.type);
        } else {
          setError('加载分享内容失败');
          setErrorType('SERVER');
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    }

    void loadShare();
  }, [token]);

  return { share, isLoading, error, errorType };
}
